import type { BodyMap } from "../../utils/bodyHierarchy";
import { logger as appLogger } from "../../utils/logger";
import { formatMjcfNumber } from "./xmlGenerationHelpers";

const DEFAULT_EQUALITY_POLYCOEF: [number, number, number, number, number] = [0, 1, 0, 0, 0];
const TENDON_ATTR_ORDER = [
  "class",
  "limited",
  "range",
  "springlength",
  "width",
  "rgba",
  "material",
  "frictionloss",
  "damping",
  "stiffness",
];

export const normalizeEqualityPolycoefForExport = (
  data: unknown,
  equalityName: string | undefined
): [number, number, number, number, number] => {
  if (!Array.isArray(data)) return DEFAULT_EQUALITY_POLYCOEF;
  const values = data.map(Number);
  if (values.length !== 5 || values.some((value) => !Number.isFinite(value))) {
    appLogger.warn(
      `[xmlGenerationActions] Replacing invalid equality polycoef for "${equalityName || "unnamed equality"}" with identity coefficients. MuJoCo requires exactly 5 finite values.`
    );
    return DEFAULT_EQUALITY_POLYCOEF;
  }
  return values as [number, number, number, number, number];
};

export const tendonAttributesToXml = ({
  attributes,
  tendonDefaultAttributesByClass,
  xmlEscapeAttr,
}: {
  attributes: Record<string, unknown> | undefined;
  tendonDefaultAttributesByClass: Map<string, Record<string, string>>;
  xmlEscapeAttr: (value: string) => string;
}): string => {
  if (!attributes || typeof attributes !== "object") return "";
  return Object.entries(attributes)
    .filter(([key, value]) => {
      if (!key || key === "name" || value === undefined || value === null) return false;
      if (key !== "class") return true;
      const className = String(value);
      if (tendonDefaultAttributesByClass.has(className)) return true;
      appLogger.warn(
        `[xmlGenerationActions] Omitting tendon class "${className}" because the generated XML has no matching tendon default class.`
      );
      return false;
    })
    .sort(([keyA], [keyB]) => {
      const indexA = TENDON_ATTR_ORDER.indexOf(keyA);
      const indexB = TENDON_ATTR_ORDER.indexOf(keyB);
      if (indexA !== -1 || indexB !== -1) {
        return (
          (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
          (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
        );
      }
      return keyA.localeCompare(keyB);
    })
    .map(([key, value]) => ` ${key}="${xmlEscapeAttr(String(value))}"`)
    .join("");
};

type SpatialTendonDef = {
  uuid: string;
  name: string;
  attributes?: Record<string, string>;
  route: Array<{
    kind: string;
    site?: string;
    geom?: string;
    sidesite?: string;
    divisor?: number;
  }>;
};

type XmlLogger = { warn: (...args: unknown[]) => void };
type JointExportInfo = {
  uuid?: string;
  name?: string;
  kind?: string | null;
  bodyUuid?: string | null;
  rootUuid?: string | null;
};
type SiteExportInfo = {
  uuid?: unknown;
  name?: unknown;
};
type TendonExportInfo = {
  name?: string;
};
type EqualityExportInfo = {
  uuid?: string;
  name?: string;
  type?: string;
  objtype?: string;
  obj1?: string;
  obj2?: string;
  active?: boolean;
  data?: unknown;
  solref?: unknown;
  solimp?: unknown;
  torquescale?: unknown;
};

export const emitTendonXml = ({
  tendonPairs,
  tendonCoefficients,
  tendonAttributesByName,
  tendonDefaultAttributesByClass,
  spatialTendonDefs,
  spatialReferencedGeomNames,
  jointXmlNameMap,
  jointInfoByUuid,
  jointBySanitizedName,
  siteExportNameByUuid,
  bodiesMap,
  findSiteByIdentifier,
  sanitizeMjcfName,
  xmlEscapeAttr,
  logger,
}: {
  tendonPairs: Array<{ joint1: string; joint2: string; tendonName: string }>;
  tendonCoefficients: Map<string, Array<{ joint: string; coef: number }>>;
  tendonAttributesByName: Map<string, Record<string, string>>;
  tendonDefaultAttributesByClass: Map<string, Record<string, string>>;
  spatialTendonDefs: SpatialTendonDef[];
  spatialReferencedGeomNames: Set<string>;
  jointXmlNameMap: Record<string, string>;
  jointInfoByUuid: Map<string, JointExportInfo>;
  jointBySanitizedName: Record<string, JointExportInfo | undefined>;
  siteExportNameByUuid: Record<string, string>;
  bodiesMap: BodyMap;
  findSiteByIdentifier: (identifier: string | undefined | null) => { uuid?: unknown } | null;
  sanitizeMjcfName: (value: string) => string;
  xmlEscapeAttr: (value: string) => string;
  logger: XmlLogger;
}): {
  tendonXml: string;
  tendonFixedChunks: string[];
  emittedTendonNames: Set<string>;
  shouldDisableDiscardVisual: boolean;
} => {
  const emittedTendonNames = new Set<string>();
  const tendonFixedChunks: string[] = [];
  const toTendonAttrXml = (attributes: Record<string, unknown> | undefined): string =>
    tendonAttributesToXml({
      attributes,
      tendonDefaultAttributesByClass,
      xmlEscapeAttr,
    });

  const emitFixedTendonFromCoefficients = (
    tendonName: string,
    joints: Array<{ joint: string; coef: number }>
  ): void => {
    if (!joints || joints.length === 0) return;
    if (emittedTendonNames.has(tendonName)) return;
    const emittedJointNames = new Set<string>();
    let body = "";
    joints.forEach((jointDef) => {
      const originalName = jointDef.joint;
      const sanitized = sanitizeMjcfName(originalName || "");
      const xmlJoint =
        (originalName && jointXmlNameMap[originalName]) ||
        (sanitized && jointXmlNameMap[sanitized]) ||
        originalName;
      if (!xmlJoint || emittedJointNames.has(xmlJoint)) return;
      const jointInfo =
        (originalName && jointInfoByUuid.get(originalName)) ||
        (sanitized && jointInfoByUuid.get(sanitized)) ||
        (xmlJoint && jointBySanitizedName[sanitizeMjcfName(xmlJoint)]) ||
        (sanitized && jointBySanitizedName[sanitized]);
      if (jointInfo && jointInfo.kind !== "hinge" && jointInfo.kind !== "slide") {
        logger.warn(
          `[xmlGenerationActions] Skipping joint "${xmlJoint}" in tendon "${tendonName}" because MuJoCo fixed tendons only support hinge or slide joints.`
        );
        return;
      }
      const rawCoef = Number(jointDef.coef);
      const coef = Number.isFinite(rawCoef) ? rawCoef : 0.5;
      emittedJointNames.add(xmlJoint);
      body += `      <joint joint="${xmlJoint}" coef="${formatMjcfNumber(coef)}"/>\n`;
    });
    if (emittedJointNames.size < 2) {
      logger.warn(
        `[xmlGenerationActions] Skipping tendon "${tendonName}" ? <fixed> requires at least 2 unique joint members.`
      );
      return;
    }
    const attrXml = toTendonAttrXml(tendonAttributesByName.get(tendonName));
    tendonFixedChunks.push(`    <fixed name="${tendonName}"${attrXml}>\n${body}    </fixed>\n`);
    emittedTendonNames.add(tendonName);
  };

  const resolveSpatialSiteXmlName = (
    ref: string | undefined,
    tendonName: string
  ): string | null => {
    if (!ref) return null;
    if (siteExportNameByUuid[ref]) return siteExportNameByUuid[ref];
    const site = findSiteByIdentifier(ref);
    if (site?.uuid && siteExportNameByUuid[String(site.uuid)]) {
      return siteExportNameByUuid[String(site.uuid)];
    }
    const fallback = sanitizeMjcfName(ref);
    if (fallback) return fallback;
    logger.warn(
      `[xmlGenerationActions] Skipping empty site reference in spatial tendon "${tendonName}".`
    );
    return null;
  };

  const resolveSpatialWrapGeomXmlName = (
    ref: string | undefined,
    tendonName: string
  ): string | null => {
    const normalizedRef = sanitizeMjcfName(ref || "");
    if (!normalizedRef) {
      logger.warn(
        `[xmlGenerationActions] Skipping empty geom reference in spatial tendon "${tendonName}".`
      );
      return null;
    }
    const matchingGeom = Object.values(bodiesMap || {})
      .flatMap((body) => (Array.isArray(body?.geoms) ? body.geoms : []))
      .find((geom) => {
        const uuid = geom?.uuid ? String(geom.uuid) : "";
        const name = geom?.name ? sanitizeMjcfName(String(geom.name)) : "";
        return uuid === ref || name === normalizedRef;
      });
    if (!matchingGeom) {
      logger.warn(
        `[xmlGenerationActions] Skipping spatial tendon "${tendonName}" wrap geom "${ref}" because the geom was not found.`
      );
      return null;
    }
    const geomAttrs = matchingGeom as { type?: unknown; kind?: unknown };
    const geomType = String(geomAttrs.type || geomAttrs.kind || "").toLowerCase();
    if (geomType !== "sphere" && geomType !== "cylinder") {
      logger.warn(
        `[xmlGenerationActions] Skipping spatial tendon "${tendonName}" wrap geom "${matchingGeom.name || ref}" because MuJoCo only supports sphere and cylinder wrap geoms.`
      );
      return null;
    }
    return sanitizeMjcfName(String(matchingGeom.name || ref));
  };

  const splitSpatialRouteBranches = (route: SpatialTendonDef["route"]) => {
    const branches: Array<SpatialTendonDef["route"]> = [];
    let current: SpatialTendonDef["route"] = [];
    for (const entry of route) {
      if (entry.kind === "pulley") {
        if (current.length > 0) branches.push(current);
        current = [];
        continue;
      }
      current.push(entry);
    }
    if (current.length > 0) branches.push(current);
    return branches;
  };

  const isValidSpatialBranch = (branch: SpatialTendonDef["route"], tendonName: string): boolean => {
    if (
      branch.length < 2 ||
      branch[0]?.kind !== "site" ||
      branch[branch.length - 1]?.kind !== "site"
    ) {
      logger.warn(
        `[xmlGenerationActions] Skipping spatial tendon "${tendonName}" because each branch must start and end with a site.`
      );
      return false;
    }
    const siteCount = branch.filter((entry) => entry.kind === "site").length;
    if (siteCount < 2) {
      logger.warn(
        `[xmlGenerationActions] Skipping spatial tendon "${tendonName}" because each branch needs at least two sites.`
      );
      return false;
    }
    for (let i = 1; i < branch.length; i += 1) {
      if (branch[i - 1]?.kind === "geom" && branch[i]?.kind === "geom") {
        logger.warn(
          `[xmlGenerationActions] Skipping spatial tendon "${tendonName}" because multiple wrap geoms must be separated by sites.`
        );
        return false;
      }
    }
    return true;
  };

  const emitSpatialTendon = (tendonDef: SpatialTendonDef): void => {
    const tendonName = tendonDef.name;
    if (!tendonName || emittedTendonNames.has(tendonName)) return;
    if (!tendonDef.route.length) {
      logger.warn(
        `[xmlGenerationActions] Skipping spatial tendon "${tendonName}" because it has no route elements.`
      );
      return;
    }
    const branches = splitSpatialRouteBranches(tendonDef.route);
    if (
      branches.length === 0 ||
      !branches.every((branch) => isValidSpatialBranch(branch, tendonName))
    ) {
      return;
    }
    let body = "";
    let routeInvalid = false;
    tendonDef.route.forEach((entry) => {
      if (routeInvalid) return;
      if (entry.kind === "site") {
        const siteName = resolveSpatialSiteXmlName(entry.site, tendonName);
        if (!siteName) {
          routeInvalid = true;
          return;
        }
        body += `      <site site="${xmlEscapeAttr(siteName)}"/>\n`;
        return;
      }
      if (entry.kind === "geom") {
        const geomName = resolveSpatialWrapGeomXmlName(entry.geom, tendonName);
        if (!geomName) {
          routeInvalid = true;
          return;
        }
        const sideSiteName = resolveSpatialSiteXmlName(entry.sidesite, tendonName);
        body += `      <geom geom="${xmlEscapeAttr(geomName)}"${
          sideSiteName ? ` sidesite="${xmlEscapeAttr(sideSiteName)}"` : ""
        }/>\n`;
        return;
      }
      if (entry.kind === "pulley") {
        const divisor = Number(entry.divisor);
        if (!Number.isFinite(divisor) || divisor <= 0) {
          logger.warn(
            `[xmlGenerationActions] Skipping spatial tendon "${tendonName}" because pulley divisor must be a positive finite number.`
          );
          routeInvalid = true;
          return;
        }
        body += `      <pulley${Number.isFinite(divisor) ? ` divisor="${formatMjcfNumber(divisor)}"` : ""}/>\n`;
      }
    });
    if (routeInvalid || !body) {
      logger.warn(
        `[xmlGenerationActions] Skipping spatial tendon "${tendonName}" because it has no exportable route elements.`
      );
      return;
    }
    const attrXml = toTendonAttrXml(tendonDef.attributes);
    tendonFixedChunks.push(`    <spatial name="${tendonName}"${attrXml}>\n${body}    </spatial>\n`);
    emittedTendonNames.add(tendonName);
  };

  if (tendonPairs.length > 0) {
    tendonPairs.forEach((pair) => {
      const joint1Info = jointBySanitizedName[pair.joint1];
      const joint2Info = jointBySanitizedName[pair.joint2];
      const joint1Uuid = joint1Info?.uuid;
      const joint2Uuid = joint2Info?.uuid;
      const bodyUuid1 = joint1Info?.bodyUuid;
      const bodyUuid2 = joint2Info?.bodyUuid;
      const xmlJoint1 =
        (joint1Uuid && jointXmlNameMap[joint1Uuid]) ||
        (bodyUuid1 && joint1Uuid && jointXmlNameMap[`${bodyUuid1}|${joint1Uuid}`]) ||
        (bodyUuid1 && joint1Info?.name && jointXmlNameMap[`${bodyUuid1}|${joint1Info.name}`]) ||
        jointXmlNameMap[pair.joint1] ||
        pair.joint1;
      const xmlJoint2 =
        (joint2Uuid && jointXmlNameMap[joint2Uuid]) ||
        (bodyUuid2 && joint2Uuid && jointXmlNameMap[`${bodyUuid2}|${joint2Uuid}`]) ||
        (bodyUuid2 && joint2Info?.name && jointXmlNameMap[`${bodyUuid2}|${joint2Info.name}`]) ||
        jointXmlNameMap[pair.joint2] ||
        pair.joint2;

      const tendonName = pair.tendonName;
      const originalTendonDef = tendonCoefficients.get(tendonName);
      let coef1 = 0.5;
      let coef2 = 0.5;

      if (originalTendonDef && originalTendonDef.length >= 2) {
        const joint1Names = [
          xmlJoint1,
          pair.joint1,
          jointBySanitizedName[pair.joint1]?.name,
        ].filter((name): name is string => typeof name === "string" && name.length > 0);
        const joint2Names = [
          xmlJoint2,
          pair.joint2,
          jointBySanitizedName[pair.joint2]?.name,
        ].filter((name): name is string => typeof name === "string" && name.length > 0);

        const joint1Match = originalTendonDef.find((joint) => {
          const origJointSanitized = sanitizeMjcfName(joint.joint);
          return joint1Names.some(
            (name) => sanitizeMjcfName(name) === origJointSanitized || name === joint.joint
          );
        });
        const joint2Match = originalTendonDef.find((joint) => {
          const origJointSanitized = sanitizeMjcfName(joint.joint);
          return joint2Names.some(
            (name) => sanitizeMjcfName(name) === origJointSanitized || name === joint.joint
          );
        });
        if (joint1Match) coef1 = joint1Match.coef;
        if (joint2Match) coef2 = joint2Match.coef;

        if (!joint1Match && !joint2Match && originalTendonDef.length === 2) {
          coef1 = originalTendonDef[0].coef;
          coef2 = originalTendonDef[1].coef;
        }
      }

      tendonFixedChunks.push(
        `    <fixed name="${tendonName}"${toTendonAttrXml(tendonAttributesByName.get(tendonName))}>\n` +
          `      <joint joint="${xmlJoint1}" coef="${formatMjcfNumber(coef1)}"/>\n` +
          `      <joint joint="${xmlJoint2}" coef="${formatMjcfNumber(coef2)}"/>\n` +
          `    </fixed>\n`
      );
      emittedTendonNames.add(tendonName);
    });
  }

  if (tendonCoefficients.size > 0) {
    for (const [tendonName, joints] of tendonCoefficients.entries()) {
      emitFixedTendonFromCoefficients(tendonName, joints);
    }
  }

  spatialTendonDefs.forEach(emitSpatialTendon);

  const tendonXml =
    tendonFixedChunks.length > 0
      ? "  <tendon>\n" + tendonFixedChunks.join("") + "  </tendon>\n\n"
      : "";

  return {
    tendonXml,
    tendonFixedChunks,
    emittedTendonNames,
    shouldDisableDiscardVisual: spatialReferencedGeomNames.size > 0,
  };
};

export const emitEqualityXml = ({
  equality,
  jointMap,
  bodiesMap,
  siteMap,
  tendonsMap,
  bodyUuidToXmlName,
  siteExportNameByUuid,
  jointXmlNameMap,
  tendonUuidToEmittedXmlName,
  emittedTendonNames,
  jointInfoByUuid,
  jointBySanitizedName,
  reserveUniqueName,
  arrayToString,
  sanitizeMjcfName,
  areBodiesInSameKinematicChain,
}: {
  equality: Record<string, EqualityExportInfo> | undefined;
  jointMap: Record<string, JointExportInfo | undefined>;
  bodiesMap: BodyMap;
  siteMap: Record<string, SiteExportInfo | undefined>;
  tendonsMap: Record<string, TendonExportInfo | undefined> | undefined;
  bodyUuidToXmlName: Record<string, string>;
  siteExportNameByUuid: Record<string, string>;
  jointXmlNameMap: Record<string, string>;
  tendonUuidToEmittedXmlName: Map<string, string>;
  emittedTendonNames: Set<string>;
  jointInfoByUuid: Map<string, JointExportInfo>;
  jointBySanitizedName: Record<string, JointExportInfo | undefined>;
  reserveUniqueName: (
    preferredRaw: string | undefined | null,
    fallbackRaw: string,
    used: Set<string>
  ) => string;
  arrayToString: (values: Array<number | string>) => string;
  sanitizeMjcfName: (value: string) => string;
  areBodiesInSameKinematicChain: (bodies: BodyMap, bodyUuidA: string, bodyUuidB: string) => boolean;
}): string => {
  const equalityArray = Object.values(equality || {}).sort((eqA, eqB) => {
    const keyA = `${eqA?.name || ""}\0${eqA?.type || ""}\0${eqA?.objtype || ""}\0${eqA?.uuid || ""}`;
    const keyB = `${eqB?.name || ""}\0${eqB?.type || ""}\0${eqB?.objtype || ""}\0${eqB?.uuid || ""}`;
    return keyA.localeCompare(keyB);
  });
  if (equalityArray.length === 0) {
    appLogger.debug(
      `[FileStore] No equality constraints to generate (equality: ${equality ? typeof equality : "undefined"}, count: ${equalityArray.length})`
    );
    return "";
  }

  appLogger.debug(
    `[FileStore] Generating equality XML for ${equalityArray.length} constraints:`,
    equalityArray.map((eq) => ({
      type: eq.type,
      objtype: eq.objtype,
      obj1: eq.obj1,
      obj2: eq.obj2,
      hasData: !!eq.data,
      hasSolref: !!eq.solref,
      hasSolimp: !!eq.solimp,
    }))
  );

  const getNameFromUuid = (
    uuid: string | undefined,
    objtype: string | undefined
  ): string | null => {
    if (!uuid || !objtype) return null;
    if (objtype === "joint") {
      const joint = jointMap[uuid];
      return joint?.name || null;
    }
    if (objtype === "body") {
      const body = (bodiesMap as Record<string, { name?: string } | undefined>)[uuid];
      return body?.name || null;
    }
    if (objtype === "site") {
      const site = siteMap[uuid];
      return typeof site?.name === "string" ? site.name : null;
    }
    if (objtype === "tendon") {
      const tendon = tendonsMap?.[uuid];
      return tendon?.name || null;
    }
    return null;
  };

  const getJointInfoForEqualityRef = (
    uuid: string | undefined,
    fallbackName: string | null
  ): JointExportInfo | null => {
    if (uuid && jointInfoByUuid.has(uuid)) return jointInfoByUuid.get(uuid) || null;
    if (fallbackName) return jointBySanitizedName[sanitizeMjcfName(fallbackName)] || null;
    return null;
  };

  let equalityXml = "  <equality>\n";
  const droppedConstraintNames: string[] = [];
  const usedEqualityNames = new Set<string>();
  const getEqualityExportName = (eq: EqualityExportInfo): string =>
    reserveUniqueName(
      eq.name,
      eq.uuid ? `eq_${String(eq.uuid).replace(/-/g, "_")}` : "equality",
      usedEqualityNames
    );

  equalityArray.forEach((eq) => {
    const name1 = getNameFromUuid(eq.obj1, eq.objtype);
    const name2 = getNameFromUuid(eq.obj2, eq.objtype);

    if (eq.type === "connect" && eq.objtype === "body" && name1 && name2) {
      if (areBodiesInSameKinematicChain(bodiesMap, String(eq.obj1), String(eq.obj2))) {
        appLogger.warn(
          `[FileStore] Skipping connect constraint "${eq.name || eq.uuid}" because body equality constraints must target different kinematic branches.`
        );
        return;
      }

      const body1Name = eq.obj1 ? bodyUuidToXmlName[String(eq.obj1)] : null;
      const body2Name = eq.obj2 ? bodyUuidToXmlName[String(eq.obj2)] : null;
      if (!body1Name || !body2Name) {
        appLogger.warn(
          `[FileStore] Skipping connect "${eq.name || eq.uuid}": body UUID not resolved in export map.`
        );
        return;
      }
      let connectLine = `    <connect body1="${body1Name}" body2="${body2Name}" name="${getEqualityExportName(eq)}"`;
      if (typeof eq.active === "boolean") {
        connectLine += ` active="${eq.active ? "true" : "false"}"`;
      }
      if (eq.data && Array.isArray(eq.data) && eq.data.length >= 3) {
        connectLine += ` anchor="${arrayToString(eq.data.slice(0, 3) as Array<number | string>)}"`;
      }
      if (eq.solimp && Array.isArray(eq.solimp) && eq.solimp.length >= 5) {
        connectLine += ` solimp="${arrayToString(eq.solimp as Array<number | string>)}"`;
      }
      if (eq.solref && Array.isArray(eq.solref) && eq.solref.length >= 2) {
        connectLine += ` solref="${arrayToString(eq.solref as Array<number | string>)}"`;
      }
      connectLine += "/>\n";
      equalityXml += connectLine;
      appLogger.debug(`[FileStore] Generated connect constraint: ${name1} <-> ${name2}`);
    } else if (eq.type === "connect" && eq.objtype === "site") {
      const site1XmlName = typeof eq.obj1 === "string" ? siteExportNameByUuid[eq.obj1] : null;
      const site2XmlName = typeof eq.obj2 === "string" ? siteExportNameByUuid[eq.obj2] : null;
      if (!site1XmlName || !site2XmlName) {
        appLogger.warn(
          `[FileStore] Skipping site-connect "${eq.name || eq.uuid}": site UUID not resolved in export map.`
        );
        return;
      }

      const findBodyForSiteInExport = (siteUuid: string): string | null => {
        for (const body of Object.values(bodiesMap)) {
          if (!body || !Array.isArray(body.sites)) continue;
          if (body.sites.map(String).includes(siteUuid)) return String(body.uuid);
        }
        return null;
      };
      const siteBody1 = findBodyForSiteInExport(String(eq.obj1));
      const siteBody2 = findBodyForSiteInExport(String(eq.obj2));
      if (
        siteBody1 &&
        siteBody2 &&
        areBodiesInSameKinematicChain(bodiesMap, siteBody1, siteBody2)
      ) {
        appLogger.warn(
          `[FileStore] Skipping site-connect "${eq.name || eq.uuid}": both sites are on the same kinematic branch.`
        );
        return;
      }
      let connectLine = `    <connect site1="${site1XmlName}" site2="${site2XmlName}" name="${getEqualityExportName(eq)}"`;
      if (typeof eq.active === "boolean") {
        connectLine += ` active="${eq.active ? "true" : "false"}"`;
      }
      if (eq.solimp && Array.isArray(eq.solimp) && eq.solimp.length >= 5) {
        connectLine += ` solimp="${arrayToString(eq.solimp as Array<number | string>)}"`;
      }
      if (eq.solref && Array.isArray(eq.solref) && eq.solref.length >= 2) {
        connectLine += ` solref="${arrayToString(eq.solref as Array<number | string>)}"`;
      }
      connectLine += "/>\n";
      equalityXml += connectLine;
    } else if (eq.type === "joint" && eq.objtype === "joint" && name1 && name2) {
      const j1s = sanitizeMjcfName(name1);
      const j2s = sanitizeMjcfName(name2);
      const jointInfo1 = getJointInfoForEqualityRef(eq.obj1, name1);
      const jointInfo2 = getJointInfoForEqualityRef(eq.obj2, name2);
      if (
        (jointInfo1 && jointInfo1.kind !== "hinge" && jointInfo1.kind !== "slide") ||
        (jointInfo2 && jointInfo2.kind !== "hinge" && jointInfo2.kind !== "slide")
      ) {
        appLogger.warn(
          `[FileStore] Skipping joint equality "${eq.name || eq.uuid}" because MuJoCo equality/joint only supports hinge or slide joints.`
        );
        return;
      }
      const mappedJ1 =
        (typeof eq.obj1 === "string" && jointXmlNameMap[eq.obj1]) || jointXmlNameMap[j1s] || j1s;
      const mappedJ2 =
        (typeof eq.obj2 === "string" && jointXmlNameMap[eq.obj2]) || jointXmlNameMap[j2s] || j2s;
      const equalityExportName = getEqualityExportName(eq);
      let jointLine = `    <joint name="${equalityExportName}"`;
      jointLine += ` joint1="${mappedJ1}" joint2="${mappedJ2}"`;
      if (typeof eq.active === "boolean") {
        jointLine += ` active="${eq.active ? "true" : "false"}"`;
      }
      jointLine += ` polycoef="${arrayToString(normalizeEqualityPolycoefForExport(eq.data, eq.name))}"`;
      if (eq.solref && Array.isArray(eq.solref) && eq.solref.length >= 2) {
        jointLine += ` solref="${arrayToString(eq.solref as Array<number | string>)}"`;
      }
      if (eq.solimp && Array.isArray(eq.solimp) && eq.solimp.length >= 5) {
        jointLine += ` solimp="${arrayToString(eq.solimp as Array<number | string>)}"`;
      }
      jointLine += "/>\n";
      equalityXml += jointLine;
      appLogger.debug(`[FileStore] Generated joint equality constraint: ${name1} <-> ${name2}`);
    } else if (eq.type === "weld" && eq.objtype === "body" && name1 && name2) {
      if (areBodiesInSameKinematicChain(bodiesMap, String(eq.obj1), String(eq.obj2))) {
        appLogger.warn(
          `[FileStore] Skipping weld constraint "${eq.name || eq.uuid}" because body equality constraints must target different kinematic branches.`
        );
        return;
      }
      const weldBody1Name = eq.obj1 ? bodyUuidToXmlName[String(eq.obj1)] : null;
      const weldBody2Name = eq.obj2 ? bodyUuidToXmlName[String(eq.obj2)] : null;
      if (!weldBody1Name || !weldBody2Name) {
        appLogger.warn(
          `[FileStore] Skipping weld "${eq.name || eq.uuid}": body UUID not resolved in export map.`
        );
        return;
      }
      let weldLine = `    <weld body1="${weldBody1Name}" body2="${weldBody2Name}" name="${getEqualityExportName(eq)}"`;
      if (typeof eq.active === "boolean") {
        weldLine += ` active="${eq.active ? "true" : "false"}"`;
      }
      if (eq.data && Array.isArray(eq.data) && eq.data.length >= 7) {
        const relpose = eq.data.slice(0, 7).map(Number);
        if (relpose.every(Number.isFinite)) {
          weldLine += ` relpose="${arrayToString(relpose)}"`;
        } else {
          appLogger.warn(
            `[FileStore] Omitting relpose for weld "${eq.name || eq.uuid}": contains non-finite values.`
          );
        }
      }
      const torquescale = Number(eq.torquescale);
      if (Number.isFinite(torquescale)) {
        weldLine += ` torquescale="${formatMjcfNumber(torquescale)}"`;
      }
      if (eq.solref && Array.isArray(eq.solref) && eq.solref.length >= 2) {
        weldLine += ` solref="${arrayToString(eq.solref as Array<number | string>)}"`;
      }
      if (eq.solimp && Array.isArray(eq.solimp) && eq.solimp.length >= 5) {
        weldLine += ` solimp="${arrayToString(eq.solimp as Array<number | string>)}"`;
      }
      weldLine += "/>\n";
      equalityXml += weldLine;
    } else if (eq.type === "tendon" && eq.objtype === "tendon") {
      const xmlTendon1 = eq.obj1 ? (tendonUuidToEmittedXmlName.get(String(eq.obj1)) ?? null) : null;
      const xmlTendon2 = eq.obj2 ? (tendonUuidToEmittedXmlName.get(String(eq.obj2)) ?? null) : null;
      if (
        !xmlTendon1 ||
        !xmlTendon2 ||
        !emittedTendonNames.has(xmlTendon1) ||
        !emittedTendonNames.has(xmlTendon2)
      ) {
        appLogger.warn(
          `[FileStore] Skipping tendon equality "${eq.name || eq.uuid}": ` +
            `referenced tendon(s) were not emitted ` +
            `(tendon1="${xmlTendon1 ?? "?"}", tendon2="${xmlTendon2 ?? "?"}").`
        );
        return;
      }
      let tendonLine = `    <tendon tendon1="${xmlTendon1}" tendon2="${xmlTendon2}" name="${getEqualityExportName(eq)}"`;
      if (typeof eq.active === "boolean") {
        tendonLine += ` active="${eq.active ? "true" : "false"}"`;
      }
      tendonLine += ` polycoef="${arrayToString(normalizeEqualityPolycoefForExport(eq.data, eq.name))}"`;
      if (eq.solref && Array.isArray(eq.solref) && eq.solref.length >= 2) {
        tendonLine += ` solref="${arrayToString(eq.solref as Array<number | string>)}"`;
      }
      if (eq.solimp && Array.isArray(eq.solimp) && eq.solimp.length >= 5) {
        tendonLine += ` solimp="${arrayToString(eq.solimp as Array<number | string>)}"`;
      }
      tendonLine += "/>\n";
      equalityXml += tendonLine;
    } else {
      const eqLabel = eq.name || eq.uuid || "unnamed";
      if (!name1 || !name2) {
        appLogger.warn(
          `[FileStore] Skipping equality "${eqLabel}" (${eq.type}/${eq.objtype}): referenced object(s) not found in scene.`
        );
      } else {
        appLogger.warn(
          `[FileStore] Skipping equality "${eqLabel}": unhandled type=${eq.type}, objtype=${eq.objtype}.`
        );
      }
      droppedConstraintNames.push(eqLabel);
    }
  });

  if (droppedConstraintNames.length > 0) {
    appLogger.warn(
      `[FileStore] ${droppedConstraintNames.length} equality constraint(s) dropped during export: ${droppedConstraintNames.join(", ")}`
    );
  }

  equalityXml += "  </equality>\n\n";
  appLogger.debug(
    `[FileStore] Generated equality XML (${equalityXml.length} chars), preview:`,
    equalityXml.substring(0, 300)
  );
  return equalityXml;
};
